'use client';

import { resetPasswordSendEmail } from '@/api/account';
import Dialog from '@/components/Dialog';
import EmailInput from '@/components/Form/EmailInput';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import Button from '@/components/Button';
import { Box } from '@mui/material';
import React, { useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';

interface ForgotPasswordProps {
  show: boolean;
  email?: string;
  onClose: () => void;
  onSuccess: (email: string) => void;
}

interface FormData {
  email: string;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  show,
  email = '',
  onClose,
  onSuccess,
}) => {
  const t = useTranslations('layout.forgotPassword');
  const [loading, setLoading] = useState(false);

  // 获取设备类型
  const { deviceType } = useContext(ResponsiveContext);
  const isMobile = deviceType === EPlatform.WAP;

  const methods = useForm<FormData>({
    defaultValues: {
      email,
    },
  });

  const { control, handleSubmit, reset, setValue } = methods;

  // 处理关闭
  const handleClose = () => {
    reset();
    onClose();
  };

  // 处理表单提交
  const onSubmit = async (data: FormData) => {
    if (loading) return;

    try {
      setLoading(true);
      const response = await resetPasswordSendEmail({ email: data.email });

      if (response.code === 200) {
        onSuccess(data.email);
      }
    } catch (error) {
      logger.log(error);
    } finally {
      setLoading(false);
    }
  };

  // 监听props变化
  React.useEffect(() => {
    if (show && email) {
      setValue('email', email);
    }
  }, [show, email, setValue]);

  // 共用的内容组件
  const Content = () => (
    <>
      {/* 标题 - 仅PC端显示 */}
      {!isMobile && (
        <h4 className="text-center mb-[12px] text-[20px] font-bb text-[#282828]">{t('title')}</h4>
      )}

      {/* 副标题 */}
      <p
        className={`text-center mb-[24px] text-[14px] text-[#808080] leading-[20px] m-0 ${
          isMobile ? 'px-[8px]' : 'px-0'
        }`}
        dangerouslySetInnerHTML={{ __html: t('subTitle') }}
      />

      {/* 表单 */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box sx={{ mb: 3 }}>
            {isMobile && (
              <p className="text-[14px] text-[#282828] leading-[14px]">{t('emailAddress')}</p>
            )}
            <EmailInput
              name="email"
              control={control}
              label={isMobile ? '' : t('emailAddress')}
              placeholder={t('emailAddress')}
              clearable
              useNativeError
              formItemClassName="mb-0"
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '46px',
                  borderRadius: isMobile ? '10px' : '6px',
                  backgroundColor: (() => {
                    return isMobile ? '#F4F4F4' : '#ffffff';
                  })(),
                },
                '& .MuiInputLabel-root': {
                  fontSize: '14px',
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '14px',
                  color: '#282828',
                  '&::placeholder': {
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>

          {/* 提交按钮 */}
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            loading={loading}
            className="w-full !font-bb !text-[16px]"
            sx={{
              height: isMobile ? '50px' : '46px',
              borderRadius: isMobile ? '50px' : '4px',
            }}
          >
            {t('submit')}
          </Button>
        </form>
      </FormProvider>
    </>
  );

  return (
    <>
      {/* PC端弹窗 */}
      <WebOnly>
        <Dialog
          open={show}
          onClose={handleClose}
          footer={null}
          sx={{
            '& .MuiDialog-paper': {
              width: '400px',
              maxWidth: '100%',
            },
            '& .MuiDialogContent-root': {
              padding: '0',
            },
          }}
        >
          <Box
            sx={{
              padding: '0 40px 56px',
            }}
          >
            <Content />
          </Box>
        </Dialog>
      </WebOnly>

      {/* 移动端半屏弹窗 */}
      <WapOnly>
        <Popup
          open={show}
          onClose={handleClose}
          position="bottom"
          height="400px"
          title="Forgot Password"
          sx={{
            zIndex: 1300,
            '& .MuiDrawer-paper': {
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              backgroundColor: '#ffffff',
            },
            '& .popup-content': {
              padding: '0',
            },
          }}
        >
          <Box
            sx={{
              p: '16px 24px 56px',
              backgroundColor: '#ffffff',
              height: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Content />
          </Box>
        </Popup>
      </WapOnly>
    </>
  );
};

export default ForgotPassword;
